import { action, observable } from 'mobx'
import { func, type IValidationSchema, validationContext } from 'validx'
import type { LegendPartDependencies } from '@/features/legends/editor/legend-part-dependencies'
import { Types } from '@/features/legends/editor/ryno-types'
import { RynoEditorViewModel } from '@/features/legends/ryno/RynoEditorViewModel'
import { when } from '@/utils/validx-util'
import { ConfigEditorViewModelBase } from './ConfigEditorViewModelBase'
import type { TxfConfig } from './legend-options'

/**
 * Whether the job is eligible for the TXF integration. The formula is only
 * validated while the integration is enabled, since the config is hidden
 * otherwise.
 */
export class TxfConfigEditorViewModel extends ConfigEditorViewModelBase<TxfConfig> {
  private readonly _schema: IValidationSchema<TxfConfigEditorViewModel> = {
    integrationEnabledExpr: [
      when(
        () => this._isEnabled(),
        func(
          () => this.integrationEnabledExpr.analyze(),
          'There are formula errors.',
        ),
      ),
    ],
  }

  /**
   * Whether the job is eligible.
   */
  readonly integrationEnabledExpr: RynoEditorViewModel

  @observable accessor showQuestionnaire = false

  readonly validation = validationContext(this, this._schema)

  constructor(
    dependencies: LegendPartDependencies,
    private readonly _isEnabled: () => boolean,
  ) {
    super(dependencies)
    this.integrationEnabledExpr = new RynoEditorViewModel({
      getSymbolTable: () => this._symbolTable,
      getNeededType: () => Types.boolean,
    })
    this.lifecycle.manage(this.integrationEnabledExpr)
  }

  /**
   * Whether the client sees the questionnaire.
   */
  @action.bound
  setShowQuestionnaire(value: boolean) {
    this.showQuestionnaire = value
  }

  /** @inheritDoc */
  @action
  hydrate(config: TxfConfig | undefined) {
    this.integrationEnabledExpr.setValue(config?.integrationEnabledExpr)
    this.showQuestionnaire = config?.showQuestionnaire ?? false
  }

  /** @inheritDoc */
  toJSON(): TxfConfig {
    return {
      integrationEnabledExpr: this.integrationEnabledExpr.toJSON(),
      showQuestionnaire: this.showQuestionnaire,
    }
  }
}
